import React, { useState } from 'react';
import { X, QrCode, User } from 'lucide-react';
import { toast } from 'sonner';

const QRCodeScanner = ({ isOpen, onClose, onSave }) => {
  const [scannedData, setScannedData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // For now, we'll parse QR code data as vCard format
      // In production, you'd use a QR code scanner library
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // Simulate QR code parsing (replace with actual QR library like jsQR)
          const text = e.target.result;
          
          // Parse vCard format from QR code
          const contact = parseVCard(text);
          
          if (contact.name || contact.email || contact.phone) {
            setScannedData(contact);
            setEditedData(contact);
            setIsEditing(false);
            toast.success('QR Code scanned successfully!');
          } else {
            toast.error('No contact information found in QR code');
          }
        } catch (error) {
          console.error('QR Parse error:', error);
          toast.error('Failed to read QR code');
        }
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('File error:', error);
      toast.error('Failed to process file');
    }
  };

  const parseVCard = (text) => {
    // Simple vCard parser
    const contact = {
      name: '',
      businessName: '',
      title: '',
      email: '',
      phone: '',
      mobile: '',
      website: '',
      address: '',
      linkedin: '',
    };

    const lines = text.split('\n');
    lines.forEach(line => {
      const [key, ...valueParts] = line.split(':');
      const value = valueParts.join(':').trim();
      
      if (key.includes('FN')) contact.name = value;
      if (key.includes('ORG')) contact.businessName = value;
      if (key.includes('TITLE')) contact.title = value;
      if (key.includes('EMAIL')) contact.email = value;
      if (key.includes('TEL') && key.includes('WORK')) contact.phone = value;
      if (key.includes('TEL') && key.includes('CELL')) contact.mobile = value;
      if (key.includes('URL')) contact.website = value;
      if (key.includes('ADR')) contact.address = value;
    });

    return contact;
  };

  const handleSave = () => {
    try {
      const existingContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
      
      const contactWithMetadata = {
        ...editedData,
        id: Date.now().toString(),
        scannedAt: new Date().toISOString(),
        source: 'qr',
      };
      
      existingContacts.unshift(contactWithMetadata);
      const trimmedContacts = existingContacts.slice(0, 100);
      
      localStorage.setItem('scannedContacts', JSON.stringify(trimmedContacts));
      
      toast.success('✅ Contact saved from QR code!');
      if (onSave) onSave(contactWithMetadata);
      handleClose();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save contact');
    }
  };

  const handleClose = () => {
    setScannedData(null);
    setEditedData({});
    setIsEditing(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl sm:rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 sm:p-6 rounded-t-2xl sm:rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold">QR Code Scanner</h2>
              <p className="text-purple-100 text-xs sm:text-sm mt-1">
                {!scannedData ? 'Scan a contact QR code' : 'Review and save contact'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {!scannedData ? (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-purple-300 rounded-xl p-8 sm:p-12 text-center hover:border-purple-500 transition-colors">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
                    <QrCode className="w-10 h-10 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Scan QR Code
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Upload a QR code image containing contact information
                    </p>
                  </div>
                  <label className="w-full max-w-md">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-xl font-semibold text-center cursor-pointer transition-colors">
                      📷 Scan QR Code
                    </div>
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center gap-3">
                <User className="w-6 h-6 text-purple-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-purple-900">Contact Found!</p>
                  <p className="text-sm text-purple-700">Review and edit if needed</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Contact Info</h3>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                  >
                    {isEditing ? 'Done' : 'Edit'}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { key: 'name', label: 'Name', icon: '👤' },
                    { key: 'businessName', label: 'Company', icon: '🏢' },
                    { key: 'title', label: 'Title', icon: '💼' },
                    { key: 'email', label: 'Email', icon: '📧' },
                    { key: 'phone', label: 'Phone', icon: '📱' },
                    { key: 'mobile', label: 'Mobile', icon: '📲' },
                    { key: 'website', label: 'Website', icon: '🌐' },
                    { key: 'address', label: 'Address', icon: '📍' },
                  ].map((field) => (
                    <div key={field.key} className="space-y-1">
                      <label className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-1">
                        <span className="text-sm">{field.icon}</span>
                        {field.label}
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedData[field.key] || ''}
                          onChange={(e) => setEditedData({ ...editedData, [field.key]: e.target.value })}
                          className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder={`Enter ${field.label.toLowerCase()}`}
                        />
                      ) : (
                        <div className="px-2 sm:px-3 py-2 bg-gray-50 rounded-lg text-gray-900 text-sm break-words">
                          {editedData[field.key] || <span className="text-gray-400 text-xs">Not available</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-4">
                <button
                  onClick={handleSave}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-4 px-6 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg"
                >
                  💾 Save Contact
                </button>
                <button
                  onClick={() => setScannedData(null)}
                  className="w-full bg-white hover:bg-gray-50 text-gray-900 py-3 px-4 rounded-xl font-semibold border-2 border-gray-300 transition-colors"
                >
                  Scan Another QR Code
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeScanner;
